import type { ReadinessOutcomeScore } from '@aigovops/core';
import { Badge, ScoreBar } from '@aigovops/design-system';

export function OutcomeCard({ outcome }: { outcome: ReadinessOutcomeScore }) {
  const dim = !outcome.applies;
  return (
    <article
      style={{
        border: '1px solid var(--border)',
        borderRadius: 'var(--radius-card)',
        background: 'var(--surface)',
        padding: 'var(--space-4)',
        display: 'grid',
        gap: 'var(--space-3)',
        opacity: dim ? 0.75 : 1,
        fontFamily: 'var(--font-ui)',
      }}
    >
      <header style={{ display: 'flex', gap: 'var(--space-3)', alignItems: 'flex-start', justifyContent: 'space-between' }}>
        <div>
          <h3 style={{ margin: 0, fontSize: 'var(--font-size-md)', color: 'var(--text)' }}>{outcome.name}</h3>
          <p style={{ margin: '4px 0 0', color: 'var(--text-dim)', fontSize: 'var(--font-size-3xs)' }}>
            {outcome.applies_reason}
          </p>
        </div>
        <Badge tone={outcome.applies ? 'accent' : 'neutral'}>{outcome.applies ? 'Applies' : 'N/A'}</Badge>
      </header>
      {outcome.applies ? (
        <>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 'var(--space-2)' }}>
            <span
              style={{
                fontFamily: 'var(--font-display)',
                fontSize: 'var(--font-size-2xl)',
                fontWeight: 600,
                color: 'var(--text)',
              }}
            >
              {outcome.score}
            </span>
            <span style={{ color: 'var(--text-dim)', fontSize: 'var(--font-size-2xs)' }}>/ 100</span>
          </div>
          <ScoreBar score={outcome.score} />
        </>
      ) : (
        // Non-applicable outcomes previously rendered as "100/100 At target"
        // green — misleading, because the customer hasn't done any work.
        // For N/A cards the score is a defaulted 100 that carries no signal;
        // don't render it, don't render a bar. Show the reason and stop.
        <div
          style={{
            color: 'var(--text-muted)',
            fontSize: 'var(--font-size-3xs)',
            fontStyle: 'italic',
          }}
        >
          Not scored — this outcome does not apply to your profile.
        </div>
      )}
      {outcome.contributing_frameworks.length > 0 ? (
        <div style={{ fontSize: 'var(--font-size-3xs)', color: 'var(--text-muted)' }}>
          <div style={{ fontWeight: 500, marginBottom: '4px', color: 'var(--text-dim)' }}>
            Contributing frameworks
          </div>
          <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'grid', gap: '2px' }}>
            {outcome.contributing_frameworks.map((f) => (
              <li
                key={f.framework_id}
                style={{ display: 'flex', justifyContent: 'space-between' }}
              >
                <span>{f.framework_name}</span>
                <span style={{ color: 'var(--text-dim)', fontFamily: 'var(--font-mono)' }}>
                  {f.score}/100 · {f.applicable_items} items
                </span>
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </article>
  );
}
